import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { Button, InlineMessages, InlineMessagesType, Eyebrow, Text, Types } from '@marriott/mi-ui-library';
import { getRfpDetailsQuery, RfpDetailsResponse } from '@marriott/mi-groups-graphql';
import { StyledEventConfirmationDetails, StyledConfirmationSection } from './EventConfirmationDetails.styles';
import { ResendQGConfirmationEmail } from './ResendQGConfirmationEmail';
import { EVENT_CONFIRMATION_LABELS } from '../../../asp-constants';
import { useLocaleStore } from '../../../../store';
import { getQueryParams, getFormattedDate } from '../../../../utils';
import { TextBlock } from '../../../../molecules';

export const EventConfirmationDetails = () => {
  const {
    resendEmailCTA,
    confirmationBanner,
    contactInfoSummary,
    resendConfirmationModal,
    errorMessage: apiErrorMessage,
    errorCode,
    errorInformation,
  } = EVENT_CONFIRMATION_LABELS;
  const { bookingDate, ...labels } = confirmationBanner;

  const [showResendConfirmationModal, setShowResendConfirmationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const { locale } = useLocaleStore();

  const [getRfpDetails, { data: eventData, loading }] = useLazyQuery<RfpDetailsResponse>(getRfpDetailsQuery);

  useEffect(() => {
    const rfpId = getQueryParams(window?.location?.search)?.['rfpid'];
    if (!rfpId) return;
    getRfpDetails({
      variables: {
        input: {
          id: rfpId,
        },
      },
      notifyOnNetworkStatusChange: true,
      context: {
        headers: {
          'accept-language': locale,
        },
      },
      fetchPolicy: 'no-cache',
    });
  }, []);

  const handleSuccessResponse = (successMessage: string) => {
    setSuccessMessage(successMessage);
    setShowResendConfirmationModal(false);
  };

  const handleErrorResponse = (errorMessage: string) => {
    setErrorMessage(errorMessage);
    setShowResendConfirmationModal(false);
  };

  const { getRfpDetailsById } = eventData?.sales?.groupSales || {};
  const addressInfo = getRfpDetailsById?.contactInfo?.address;
  const address = [
    addressInfo?.line1,
    addressInfo?.line2,
    addressInfo?.city,
    addressInfo?.state,
    addressInfo?.zipCode,
    addressInfo?.country,
  ]
    .filter(Boolean)
    .join(', ');

  const fullName = [getRfpDetailsById?.contactInfo?.firstName, getRfpDetailsById?.contactInfo?.lastName]
    .filter(Boolean)
    .join(' ');

  if (getRfpDetailsById?.code === apiErrorMessage && getRfpDetailsById?.httpStatus === errorCode) {
    return (
      <div className="d-flex justify-content-center align-items-center my-4">
        <Text
          customClass="text-nowrap "
          copyText={errorInformation}
          fontSize={Types.size.medium}
          element={Types.tags.paragraph}
        />
      </div>
    );
  }

  return (
    <StyledEventConfirmationDetails
      data-testid="groups-EventConfirmationDetails"
      data-component-name="o-groups-EventConfirmationDetails"
    >
      {errorMessage && (
        <div className="mb-4">
          <InlineMessages type={InlineMessagesType.Error} title={errorMessage} severity="1" />
        </div>
      )}
      {successMessage && (
        <div className="mb-4 mt-4">
          <InlineMessages type={InlineMessagesType.Success} title={successMessage} severity="1" />
        </div>
      )}
      {loading ? (
        <div className="text-center mb-5">
          <div className="m-spinner-m" />
        </div>
      ) : (
        <div className="mb-3">
          <StyledConfirmationSection className="d-flex p-4 p-md-5 confirmation-body">
            <div className="pb-4 pb-md-0 pr-md-4 property-details">
              <div className="mb-3">
                <Button
                  callback={() => setShowResendConfirmationModal(prev => !prev)}
                  className="m-button-l m-button-primary button"
                  ariaLabel={resendEmailCTA}
                >
                  {resendEmailCTA}
                </Button>
              </div>
              <TextBlock
                label={labels.confirmationNumber}
                value={getRfpDetailsById?.id ?? ''}
                customClass="text-block"
              />
            </div>
            <div className="pt-4 pt-md-0 pl-md-4 booking-details">
              <div className="row">
                <TextBlock
                  label={bookingDate}
                  value={`${getFormattedDate(moment(getRfpDetailsById?.startDate as string))} - ${getFormattedDate(
                    moment(getRfpDetailsById?.endDate as string)
                  )}`}
                  customClass="col-12 text-block"
                />
                <TextBlock
                  label={labels.eventName}
                  value={getRfpDetailsById?.eventName ?? ''}
                  customClass="col-6 text-block"
                />
                <TextBlock
                  label={labels.eventType}
                  value={getRfpDetailsById?.eventType ?? ''}
                  customClass="col-6 text-block"
                />

                <TextBlock
                  label={labels.guestRooms}
                  value={`${getRfpDetailsById?.guestRooms ?? 0}`}
                  customClass="col-6 text-block"
                />

                <TextBlock
                  label={labels.eventSpace}
                  value={`${getRfpDetailsById?.attendees ?? 0}`}
                  customClass="col-6 text-block"
                />
              </div>
            </div>
          </StyledConfirmationSection>
          <StyledConfirmationSection className="mt-3 standard">
            <div className="t-subtitle-xl">{contactInfoSummary.title}</div>
            <div className="row">
              <TextBlock label={contactInfoSummary.contact} value={fullName} customClass="col-md-6 col-xl-4" />
              <div className="pt-4 pt-md-5 col-md-6 col-xl-4">
                <Eyebrow text={contactInfoSummary.phoneAndEmail} />
                <Text
                  element={Types.tags.div}
                  copyText={getRfpDetailsById?.contactInfo?.phone}
                  fontSize={Types.size.medium}
                  customClass={clsx('item-value', 'ltr-number')}
                />
                <Text
                  element={Types.tags.div}
                  copyText={getRfpDetailsById?.contactInfo?.email}
                  fontSize={Types.size.medium}
                  customClass={clsx('item-value')}
                />
              </div>

              <TextBlock label={contactInfoSummary.address} value={address} customClass="col-md-6 col-xl-4" />
            </div>
          </StyledConfirmationSection>
        </div>
      )}
      {showResendConfirmationModal && (
        <ResendQGConfirmationEmail
          rfpId={getRfpDetailsById?.id ?? ''}
          show={showResendConfirmationModal}
          onClose={() => setShowResendConfirmationModal(false)}
          labels={resendConfirmationModal}
          handleSuccessResponse={handleSuccessResponse}
          handleErrorResponse={handleErrorResponse}
        />
      )}
    </StyledEventConfirmationDetails>
  );
};
